import { Tag } from '@jouzen/ecom-components/v3/Tag';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

import TestimonialCardFooter from './TestimonialCardFooter';
import type {
  LandingPageTestimonial,
  LandingPageTestimonialsTheme,
} from './types';
import getCardClasses from './utils/getCardClasses';

export interface DesktopTestimonialCardProps extends LandingPageTestimonial {
  readonly activeIndex: number;
  readonly index: number;
  readonly inverted: boolean;
  readonly numberOfTestimonials: number;
  readonly onActiveIndexChange: (index: number) => void;
  readonly theme: LandingPageTestimonialsTheme;
}

const DesktopTestimonialCard = ({
  activeIndex,
  action,
  company,
  id,
  image,
  index,
  inverted,
  name,
  nameMsgKey,
  numberOfTestimonials,
  onActiveIndexChange,
  quote,
  tag,
  theme,
}: DesktopTestimonialCardProps) => {
  const t = useTranslations();
  const hasFooter = !!nameMsgKey || !!name || !!company || !!action;

  return (
    <>
      <AnimatePresence>
        {activeIndex === index && (
          <Motion
            animate={{
              width: numberOfTestimonials == 2 ? '33.3%' : '25%',
              x: 0,
              opacity: 1,
              display: 'flex',
            }}
            className={cx(
              'rounded-l-md bg-sandstone-400 p-4 xl:p-8',
              getCardClasses({ inverted, theme }),
            )}
            data-cy="landing-page-testimonials-desktop-card-content"
            initial={{ width: 0, x: 50, opacity: 0, display: 'none' }}
            exit={{ width: 0, x: 50, display: 'none' }}
            key={id}
            transition={{ ease: 'easeInOut', duration: 0.2 }}
          >
            <Motion
              className={cx(
                'flex-col justify-between',
                getCardClasses({ inverted, theme }),
              )}
              initial={{
                opacity: 0,
                x: 10,
                height: 0,
                display: 'none',
              }}
              animate={{
                opacity: 1,
                x: 0,
                height: 'auto',
                transition: { ease: 'easeInOut', duration: 0.2, delay: 0.3 },
                display: 'flex',
              }}
              exit={{
                x: 10,
                height: 0,
                display: 'none',
                transition: { duration: 0.0, delay: 0.0 },
              }}
            >
              <div>
                <Tag variant={theme}>{t(tag)}</Tag>
              </div>
              <blockquote>
                <Typography
                  className="mb-4 text-body-lg leading-tight before:block before:content-[open-quote] after:content-[close-quote] xxl:text-heading-sm"
                  color="inherit"
                  Element="p"
                  font="serif"
                  weight="thin"
                >
                  <RichText messageKey={quote} />
                </Typography>
                {hasFooter && (
                  <footer>
                    <TestimonialCardFooter
                      action={action}
                      company={company}
                      name={name}
                      nameMsgKey={nameMsgKey}
                    />
                  </footer>
                )}
              </blockquote>
            </Motion>
          </Motion>
        )}
      </AnimatePresence>
      <Image
        className={cx(
          'z-10 mr-4 cursor-pointer rounded-md object-cover last:mr-0',
          {
            'w-1/3': numberOfTestimonials === 2,
            'w-1/4': numberOfTestimonials !== 2,
          },
        )}
        data-cy="landing-page-testimonials-desktop-card-img"
        height={numberOfTestimonials === 2 ? 500 : 429}
        localizedAlt={image.alt}
        onClick={() => onActiveIndexChange(index)}
        onMouseEnter={() => onActiveIndexChange(index)}
        src={image.src}
        width={numberOfTestimonials === 2 ? 434 : 322}
      />
    </>
  );
};

export default DesktopTestimonialCard;
